import { useContext, useEffect, useRef, useState } from 'react'
import type { ChangeEvent, MouseEvent } from 'react'
import { observer } from 'mobx-react-lite'
import { isAxiosError } from 'axios'
import styled from 'styled-components'

import { MODALS_ID, PAGE_NAME, SUBSCRIPTION_VALIDATE, SUBSCRIPTION_VALIDATE_MESSAGE } from '../../constants'
import type { SUBSCRIPTION_VALIDATE_KEY_TYPES } from '../../constants'
import type { components } from '../../constants/schema'

import { toast, HttpReq } from '../../utils'
import { abrStartSubscriptionProduct } from '../../utils/abr'

import { StoresContext } from '../../provider/StoreProvider'
import { ModalContext } from '../../provider/modalProvider'

import { useNavigate } from '../../hooks/useNavigate'

import { Wrap } from '../../components/container/Wrap'
import { Content } from '../../components/container/Content'
import { AlignCenterItem, RowBetween } from '../../components/common'
import { Button } from '../../components/button'
import { ProductInfoCard } from '../../components/card/ProductInfoCard'
import { Text } from '../../components/text'
import { DivideLine } from '../../components/divide'
import { Footer, FOOTER_MIN_HEIGHT } from '../../components/footer'
import { Header } from '../../components/header'
import { NoticeContainer } from '../../components/noticeContainer/NoticeContainer'
import { TermsDetailModal } from '../../components/modals/TermsDetailModal'
import { OrderLoadingModal } from './modals/OrderLoadingModal'

import { Color } from '../../theme/color'

import { ReactComponent as CheckedIcon } from '../../assets/ico/common/check/check_checked_blue_fill.svg'
import { ReactComponent as UncheckedIcon } from '../../assets/ico/common/check/check_default_gray_fill.svg'
import { ReactComponent as ArrowIcon } from '../../assets/ico/common/arrow/right/arrow_right_gray-04_medium.svg'

type ProductDetail = components['schemas']['ProductDetailDto']
type DepositAccount = components['schemas']['DepositAccountDto']
type Terms = components['schemas']['TermsDto'][]

const LIMIT_PRICE = 30000000

export const Order = observer(() => {
  const {
    commonStore: { setInvalidAccess },
  } = useContext(StoresContext)
  const { openModal, closeModal } = useContext(ModalContext)

  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [productDetail, setProductDetail] = useState<ProductDetail | null>(null)
  const [depositAccount, setDepositAccount] = useState<DepositAccount | null>(null)
  const [terms, setTerms] = useState<Terms | null>(null)

  const [amountValue, setAmountValue] = useState<number | undefined>() // 청약 신청 수량
  const [validateStatus, setValidateStatus] = useState<SUBSCRIPTION_VALIDATE_KEY_TYPES>(SUBSCRIPTION_VALIDATE.INPUT) // footer button status
  const [textWidth, setTextWidth] = useState<number | undefined>() // 청약 신청 수량 text width (style)
  const [checkTerms, setCheckTerms] = useState<{ id: number; checked: boolean; required: boolean }[]>([]) // 동의 여부
  const [termsId, setTermsId] = useState<number | undefined>()

  const navigate = useNavigate()

  const inputTextRef = useRef<HTMLSpanElement | null>(null)

  const productId = Number(location.pathname.split('/')[3])
  const balance = depositAccount?.balance ?? 0 // 예치금
  const offerPrice = productDetail?.activeProductSale?.offerPrice ?? 0 // 개당 가격
  const residualDeposit = balance - offerPrice * (amountValue ?? 0) // 잔여 예치금
  const resultAmount = offerPrice * (amountValue ?? 0) // 총 신청 금액

  const openTermsModal = () => {
    openModal({
      id: MODALS_ID.TERMS_DETAIL,
      Component: () => (
        <TermsDetailModal
          termsId={termsId}
          isScrollEvent={true}
          buttonProps={{
            footerContent: '해당 내용을 확인했으며 동의해요',
            type: 'primary',
          }}
          onDismiss={(result) => {
            setTermsId(undefined)
            closeModal(MODALS_ID.TERMS_DETAIL)
            if (!result) return

            const isRequired = terms?.find(({ id }) => id === termsId)?.required ?? false
            setCheckTerms((prevState) =>
              prevState.map((v) =>
                v.id === termsId ? { id: Number(termsId), checked: true, required: isRequired } : v,
              ),
            )
          }}
        />
      ),
    })
  }

  const openOrderLoadingModal = () => {
    openModal({
      id: MODALS_ID.ORDER_LOADING,
      Component: () => (
        <OrderLoadingModal
          saleData={{
            id: productId,
            count: amountValue ?? 0,
            terms: checkTerms.map(({ id, checked }) => ({ termsId: id, approved: checked })),
          }}
          success={() => navigate(`/market/product/${productId}?successOrder=true`)}
          onDismiss={() => closeModal(MODALS_ID.ORDER_LOADING)}
        />
      ),
    })
  }

  useEffect(() => {
    loadData()
  }, [])

  // 청약 신청하기 상태가 아니면 에러
  useEffect(() => {
    if (productDetail) {
      if (productDetail.subscriptionStatus === 'SUBSCRIPTION_AVAILABLE') {
        setIsLoading(false)
        return
      }
      setInvalidAccess(true)
    }
  }, [productDetail])

  // 약관 초기값 셋팅
  useEffect(() => {
    if (!terms) return
    setCheckTerms(terms.map(({ id, required }) => ({ id, required, checked: false })))
  }, [terms])

  useEffect(() => {
    if (typeof termsId === 'undefined') return
    openTermsModal()
  }, [termsId])

  // status 관리
  useEffect(() => {
    const total = offerPrice * (amountValue ?? 0)
    if (!amountValue) {
      setValidateStatus(SUBSCRIPTION_VALIDATE.INPUT)
    } else if (residualDeposit < 0 || total > Number(balance)) {
      setValidateStatus(SUBSCRIPTION_VALIDATE.BALANCE)
    } else if (
      resultAmount > LIMIT_PRICE ||
      resultAmount > (productDetail?.activeProductSale?.subscriptionLimitPrice ?? 0)
    ) {
      setValidateStatus(SUBSCRIPTION_VALIDATE.OVER_AMOUNT)
    } else if (!checkTerms.length || !checkTerms.filter(({ required }) => required).every((v) => v.checked)) {
      setValidateStatus(SUBSCRIPTION_VALIDATE.TERMS)
    } else {
      setValidateStatus(SUBSCRIPTION_VALIDATE.OKAY)
    }
  }, [amountValue, checkTerms])

  useEffect(() => {
    setTextWidth(amountValue ? inputTextRef.current?.clientWidth : 0)
  }, [amountValue, inputTextRef])

  // server data load
  const loadData = async () => {
    try {
      const productDetailResponse = await HttpReq<ProductDetail>({
        method: 'GET',
        url: `/product/${productId}`,
      })
      const depositAccountResponse = await HttpReq<DepositAccount>({
        method: 'GET',
        url: `/user/current/depositAccount`,
      })
      const termsResponse = await HttpReq<Terms>({
        method: 'GET',
        url: `/terms/page/${PAGE_NAME.PURCHASE}`,
      })

      setProductDetail(productDetailResponse)
      setDepositAccount(depositAccountResponse)
      setTerms(termsResponse)

      abrStartSubscriptionProduct(productDetailResponse)
    } catch (error) {
      console.log({ error })
      if (isAxiosError(error)) {
        if (error.response?.data.CODE === 'API_000404') {
          setInvalidAccess(true)
          return
        }
      }
      toast('페이지를 불러오는데 실패했습니다.', error)
    }
  }

  if (isLoading) {
    return <></>
  }

  // input값 수정
  const handleAmount = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
    if (value.includes('.') || value.includes('-') || Number(value) > LIMIT_PRICE) return
    setAmountValue(Number(value))
  }

  // 청약 수량 설정
  const incrementAmount = ({ currentTarget: { name } }: MouseEvent<HTMLButtonElement>) => {
    switch (name) {
      case '10':
        setAmountValue((prevState) => (prevState || 0) + 10)
        return
      case '50':
        setAmountValue((prevState) => (prevState || 0) + 50)
        return
      case '100':
        setAmountValue((prevState) => (prevState || 0) + 100)
        return
      case 'max':
        setAmountValue(maximumCount())
        return
      default:
        return
    }
  }

  // 최대 신청 가능 수량
  const maximumCount = () => {
    if (!productDetail || !productDetail.activeProductSale) return 0

    const {
      limit, // 인당 판매 제한 개수
      remainCount, // 잔여 갯수
      offerPrice, // 개당 가격
      subscriptionLimitPrice, // 청약 한도 금액
    } = productDetail.activeProductSale

    if (remainCount * offerPrice > subscriptionLimitPrice) {
      return Math.floor(subscriptionLimitPrice / offerPrice)
    }

    return limit
  }

  return (
    <Wrap>
      <Header title="청약 신청하기" type="SUB" />
      <StyledContent backgroundColor="gray_1">
        {/* 청약 정보 */}
        <ProductInfoContainer>
          <ProductInfoCard>
            <Text size={18} weight={600}>
              청약 정보
            </Text>
            <Text size={16} color="text_secondary">
              {productDetail?.artist} • {productDetail?.name}
            </Text>
            <RowBetween>
              <Text size={16} color="text_secondary">
                총 발행 금액
              </Text>
              <Text size={16} weight={500}>
                {productDetail?.activeProductSale?.totalIssuedPrice?.toLocaleString()}원
              </Text>
            </RowBetween>
            <RowBetween>
              <Text size={16} color="text_secondary">
                주당 공모가 액
              </Text>
              <Text size={16} weight={500}>
                {offerPrice?.toLocaleString()}원
              </Text>
            </RowBetween>
          </ProductInfoCard>
        </ProductInfoContainer>
        {/* 구매 수량 입력 및 약관 동의 */}
        <OrderInfoContainer>
          <AmountInputContainer textWidth={textWidth}>
            <span ref={inputTextRef}>{amountValue}</span>
            <AmountInput
              type="number"
              inputMode="numeric"
              pattern="[0-9]*"
              value={amountValue || ''}
              placeholder="수량을 입력해주세요."
              onChange={handleAmount}
              onKeyDown={(e) => {
                const notNumberArr = ['.', '-', '+', '*', '#']
                const isNotNumber = notNumberArr.includes(e.key)
                if (isNotNumber) e.preventDefault()
              }}
            />
            <AmountInputActiveBorder />
          </AmountInputContainer>
          <AmountButtonBox>
            <AmountButton name="10" onClick={incrementAmount}>
              +10주
            </AmountButton>
            <AmountButton name="50" onClick={incrementAmount}>
              +50주
            </AmountButton>
            <AmountButton name="100" onClick={incrementAmount}>
              +100주
            </AmountButton>
            <AmountButton name="max" onClick={incrementAmount}>
              최대 수량
            </AmountButton>
          </AmountButtonBox>
          <CostContainer>
            <ResultAmountBox>
              <RowBetween>
                <Text size={18} weight={600}>
                  총 신청 금액
                </Text>
                <Text size={18} weight={600} color="primary_5">
                  {resultAmount.toLocaleString()}원
                </Text>
              </RowBetween>
              {residualDeposit < 0 ? (
                <Text size={12} color="error">
                  보유한 {SUBSCRIPTION_VALIDATE_MESSAGE[SUBSCRIPTION_VALIDATE.BALANCE]}
                </Text>
              ) : resultAmount > LIMIT_PRICE ||
                resultAmount > (productDetail?.activeProductSale?.subscriptionLimitPrice ?? 0) ? (
                <Text size={12} color="error">
                  {SUBSCRIPTION_VALIDATE_MESSAGE[SUBSCRIPTION_VALIDATE.OVER_AMOUNT]}
                </Text>
              ) : (
                <></>
              )}
            </ResultAmountBox>
            <RowBetween>
              <Text size={16} color="text_secondary">
                보유 예치금
              </Text>
              <Text size={16} weight={500}>
                {balance?.toLocaleString()}원
              </Text>
            </RowBetween>
            <RowBetween>
              <Text size={16} color="text_secondary">
                잔여 예치금
              </Text>
              <Text size={16} weight={500}>
                {residualDeposit < 0 ? 0 : residualDeposit.toLocaleString()}원
              </Text>
            </RowBetween>
          </CostContainer>
          <DivideLine />
          <TermsContainer>
            {terms?.map(({ id, name, required }) => {
              const term = checkTerms.find((term) => term.id === id)
              const isChecked = term?.checked ?? false

              return (
                <TermsItem
                  key={id}
                  onClick={() =>
                    isChecked
                      ? setCheckTerms((prevState) =>
                          prevState.map((terms) => (terms.id === id ? { ...terms, checked: false } : terms)),
                        )
                      : setTermsId(id)
                  }
                >
                  {isChecked ? <CheckedIcon /> : <UncheckedIcon />}
                  <Text size={14} color={isChecked ? 'text_primary' : 'text_secondary'}>
                    ({required ? '필수' : '선택'}) {name}
                  </Text>
                  <ArrowIcon />
                </TermsItem>
              )
            })}
          </TermsContainer>
        </OrderInfoContainer>
        <NoticeContainer
          title="청약 주의 사항"
          list={[
            '청약 취소는 청약 기간 내에만 가능해요.',
            '최대 청약 신청 금액은 3,000만 원을 초과할 수 없어요.',
            '모집에 대한 청약 경쟁률, 청약 참여자 수 등에 따라 청약을 신청한 증권 수 보다 적은 수의 증권을 배정받을 수 있어요. 이때, 잔여 청약 증거금은 투자자의 NH 농협 예치금 계좌로 환불 처리됩니다.',
          ]}
        />
      </StyledContent>
      <Footer>
        <Button
          type={SUBSCRIPTION_VALIDATE.OKAY === validateStatus ? 'primary' : 'default'}
          size="large"
          disabled={SUBSCRIPTION_VALIDATE.OKAY !== validateStatus}
          block={true}
          {...(SUBSCRIPTION_VALIDATE.OKAY ? { onClick: () => openOrderLoadingModal() } : undefined)}
          // {...(VALIDATE_STATUS_TRADE.OKAY === validateStatus ? LoadingModal : undefined)}
        >
          {SUBSCRIPTION_VALIDATE_MESSAGE[validateStatus]}
        </Button>
      </Footer>
    </Wrap>
  )
})

const StyledContent = styled(Content)`
  padding-bottom: ${FOOTER_MIN_HEIGHT}px;
`

const ProductInfoContainer = styled.div`
  padding: 16px;
`

const OrderInfoContainer = styled.div`
  padding: 40px 24px 16px;
  border-top-left-radius: 24px;
  border-top-right-radius: 24px;
  background-color: ${Color.white};
`

const AmountInputActiveBorder = styled.div`
  position: absolute;
  bottom: 0;
  left: 50%;
  width: 0;
  height: 1px;
  border-bottom: 2px solid ${Color.primary_5};
  transform: translateX(-50%);
`

const AmountInputContainer = styled.div<{ textWidth?: number }>`
  display: block;
  position: relative;
  width: 100%;
  &::before {
    display: block;
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 1px;
    border-bottom: 2px solid ${Color.line};
  }
  &::after {
    display: ${({ textWidth }) => (textWidth ? 'block' : 'none')};
    content: '주';
    position: absolute;
    top: 50%;
    left: calc(50% + 8px + ${({ textWidth }) => textWidth}px / 2);
    font-size: 30px;
    font-weight: 600;
    color: ${Color.text_primary};
    transform: translate(-50%, -50%);
  }
  > input:focus ~ ${AmountInputActiveBorder} {
    width: 100%;
    transition: 0.25s;
  }
  > span {
    opacity: 0;
    position: absolute;
    font-size: 30px;
    font-weight: 600;
  }
  ${({ textWidth }) =>
    textWidth
      ? `
        > input {
          width: calc(100% - 26px);
        }
      `
      : ''}
`

const AmountInput = styled.input`
  width: 100%;
  padding: 8px;
  outline: none;
  border: none;
  font-size: 30px;
  font-weight: 600;
  text-align: center;
  color: ${Color.text_primary};
  &::placeholder {
    font-size: 30px;
    font-weight: 500;
    color: ${Color.gray_4};
  }
`

const AmountButtonBox = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 32px;
`

const AmountButton = styled.button`
  padding: 8px 16px;
  border: 1px solid ${Color.gray_3};
  border-radius: 4px;
  background-color: ${Color.white};
  font-size: 14px;
  font-weight: 500;
  color: ${Color.text_primary};
`

const CostContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 64px 0 16px;
`

const ResultAmountBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  > ${RowBetween} {
    width: 100%;
  }
`

const TermsContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 16px;
`

const TermsItem = styled(AlignCenterItem)`
  padding: 8px 0;
  > svg {
    margin-right: 8px;
  }
`
